/**
 * Quick Actions Menu (Wave H batch 2): a persona-aware "+" menu in the top bar.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * Owner-curated actions:
 * - Contractor portal users: Raise an RFI, Propose a date, Attach a document
 * - Operator QC lead and engineer: Request an inspection, Plan an activity, Record an observation
 * - Power user: Register a project
 * - Inspector: Conduct my next agreed inspection
 * - Everyone: My tasks
 *
 * Each entry deep links into the existing flow with sensible prefill. Entries the
 * persona's policy verbs do not allow are never rendered. Keyboard accessible:
 * Space/Enter/ArrowDown to open, ArrowUp/ArrowDown navigation, Escape to close.
 */

import { Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router";

import type { Me } from "../api/me";
import { strings } from "../brand/strings";

export interface QuickActionItem {
  readonly id: string;
  readonly label: string;
  readonly href: string;
}

export function getQuickActions(
  me: Me | null,
  configurationAllowed: boolean,
): readonly QuickActionItem[] {
  if (me === null) {
    return [];
  }
  const items: QuickActionItem[] = [];
  const wf = me.workflowClass;

  // Contractor portal users
  if (wf === "contractor") {
    items.push(
      {
        id: "raise-rfi",
        label: strings.quickActions.raiseRfi,
        href: "/itps?act=rfi",
      },
      {
        id: "propose-date",
        label: strings.quickActions.proposeDate,
        href: "/calendar",
      },
      {
        id: "attach-document",
        label: strings.quickActions.attachDocument,
        href: "/portfolio/projects?act=attach",
      },
    );
  }

  // Operator QC lead and engineer
  if (wf === "qc_lead" || wf === "planner") {
    items.push(
      {
        id: "request-inspection",
        label: strings.quickActions.requestInspection,
        href: "/programs/construction?act=request",
      },
      {
        id: "plan-activity",
        label: strings.quickActions.planActivity,
        href: "/calendar?act=plan",
      },
      {
        id: "record-observation",
        label: strings.quickActions.recordObservation,
        href: "/findings?act=record",
      },
    );
  }

  // Power user
  if (wf === "manager" || (configurationAllowed && wf !== "contractor" && wf !== "inspector")) {
    items.push({
      id: "register-project",
      label: strings.quickActions.registerProject,
      href: "/portfolio/projects?act=register",
    });
  }

  // Inspector
  if (wf === "inspector") {
    items.push({
      id: "conduct-inspection",
      label: strings.quickActions.conductInspection,
      href: "/inspections?queue=mine",
    });
  }

  // Everyone
  items.push({
    id: "my-tasks",
    label: strings.quickActions.myTasks,
    href: "/tasks",
  });

  return items;
}

export function QuickActionsMenu({
  me,
  configurationAllowed,
}: {
  readonly me: Me | null;
  readonly configurationAllowed: boolean;
}): React.JSX.Element | null {
  // Wave Experience A: the menu is open for ONE history entry. Any navigation (Back, a
  // sidebar link, a link inside the page) moves the key and the menu is closed with it.
  const location = useLocation();
  const [openAt, setOpenAt] = useState<string | null>(null);
  const open = openAt === location.key;
  const setOpen = (next: boolean | ((prev: boolean) => boolean)): void => {
    const value = typeof next === "function" ? next(open) : next;
    setOpenAt(value ? location.key : null);
  };
  const [focusedIndex, setFocusedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLAnchorElement | null)[]>([]);

  const items = getQuickActions(me, configurationAllowed);

  // Close when clicking outside
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onPointerDown = (event: MouseEvent | TouchEvent): void => {
      if (containerRef.current !== null && !containerRef.current.contains(event.target as Node)) {
        setOpenAt(null);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [open]);

  // Focus the active item when open or focusedIndex changes
  useEffect(() => {
    if (open && itemRefs.current[focusedIndex]) {
      itemRefs.current[focusedIndex].focus();
    }
  }, [open, focusedIndex]);

  if (items.length === 0) {
    return null;
  }

  const handleButtonKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>): void => {
    if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setFocusedIndex(0);
      setOpen(true);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setFocusedIndex(items.length - 1);
      setOpen(true);
    }
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setFocusedIndex((prev) => (prev + 1) % items.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setFocusedIndex((prev) => (prev - 1 + items.length) % items.length);
    } else if (event.key === "Home") {
      event.preventDefault();
      setFocusedIndex(0);
    } else if (event.key === "End") {
      event.preventDefault();
      setFocusedIndex(items.length - 1);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      buttonRef.current?.focus();
    } else if (event.key === "Tab") {
      setOpen(false);
    } else if (event.key === " " && itemRefs.current[focusedIndex]) {
      event.preventDefault();
      itemRefs.current[focusedIndex].click();
    }
  };

  return (
    <div ref={containerRef} className="q-quick-actions q-account">
      <button
        ref={buttonRef}
        type="button"
        className="q-button q-button--quiet"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls="q-quick-actions-menu"
        aria-label={strings.quickActions.buttonLabel}
        data-testid="quick-actions-toggle"
        onClick={() => {
          setOpen((prev) => !prev);
          setFocusedIndex(0);
        }}
        onKeyDown={handleButtonKeyDown}
      >
        <Plus className="q-shell__bar-icon" aria-hidden="true" strokeWidth={1.75} />
        <span className="q-shell__bar-label">{strings.quickActions.buttonLabel}</span>
      </button>

      {open && (
        <div
          role="menu"
          id="q-quick-actions-menu"
          aria-label={strings.quickActions.menuLabel}
          className="q-account__menu"
          data-testid="quick-actions-menu"
          style={{ minWidth: "15rem" }}
          onKeyDown={handleMenuKeyDown}
        >
          {items.map((item, index) => (
            <Link
              key={item.id}
              ref={(el) => {
                itemRefs.current[index] = el;
              }}
              to={item.href}
              role="menuitem"
              tabIndex={focusedIndex === index ? 0 : -1}
              className="q-button q-button--quiet"
              style={{
                justifyContent: "flex-start",
                textAlign: "left",
                width: "100%",
                whiteSpace: "nowrap",
                textDecoration: "none",
                color: "inherit",
              }}
              data-testid={`quick-action-${item.id}`}
              onClick={() => {
                setOpen(false);
              }}
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
